import { CauseName } from '@beyond-js/ui';
import { ApiProblem } from '@cdn/api';
import { Observable } from '@cdn/state';

/** Who did not answer: CDN's own service, a product it named for the page, or one it does not name */
export type Cause = 'cdn' | 'accounts' | 'projects' | 'snapshots' | 'upstream';

/** The evidence of a read that did not answer: its request, when it failed and the service's words */
export interface Evidence {
	request: string | null;
	time: string;
	text: string;
}

/** One read of the page, as its register holds it */
export interface Read {
	/** What the read is, as a noun ("Releases"): said only in the notice's Technical details */
	title: string;
	/** Who did not answer its last load; null while it answers */
	cause: Cause | null;
	/** Whether it shows content kept from an earlier read */
	kept: boolean;
	details: Evidence | null;
	/** The read again; it settles once tried */
	run: () => Promise<unknown>;
}

/** One notice: who did not answer, and the reads that did not answer because of it */
export interface Said {
	cause: Cause;
	reads: Array<{ key: string; title: string; details: Evidence | null; run: () => Promise<unknown> }>;
}

/**
 * The outages of the page in view (decision D163): which of its reads did not answer, and who did not answer
 * each one. The reads of one cause are one notice, said once after the page's header: who did not answer, when it
 * last tried and one Try again, listing nothing; each read says it is not available in its own place. A read is held
 * while it is mounted, answering or not, so the register knows when nothing on a page that declared its body could
 * be read (`whole`): the notice is then the page's body.
 *
 * Only reads are held, never a change: a change whose answer did not arrive keeps its unknown outcome. A session
 * that ended is the page's `Session`'s and a refusal is an answer: neither is an outage, and neither is held as one.
 */
export class Outages extends Observable {
	/** The products the service names when one of them did not answer (`details.upstream`, `details.provider`) */
	static #named: Record<string, Cause> = { accounts: 'accounts', projects: 'projects', snapshots: 'snapshots' };

	#reads = new Map<string, Read>();
	#bodies = 0;
	#regions = 0;

	/**
	 * Who did not answer a load, or null for anything else: an answer, a refusal, a session that ended, a change
	 * whose outcome is unknown. Grouped by the dependency the answer names, never by its words or its status alone.
	 */
	static cause(problem: unknown): Cause | null {
		if (problem === undefined || problem === null) return null;
		const read = ApiProblem.of(problem);
		if (!read.outage || read.quiet || read.unknown) return null;
		return Outages.who(read);
	}

	/**
	 * Who did not answer a problem that is an outage, a change's included (decision D186): CDN's own service, the
	 * product the service named, or a service it does not name
	 */
	static who(problem: unknown): Cause {
		const read = ApiProblem.of(problem);
		if (!read.is('UPSTREAM_UNAVAILABLE')) return 'cdn';
		const named = read.details?.upstream ?? read.details?.provider;
		return Outages.#named[String(named)] ?? 'upstream';
	}

	/**
	 * How a change that met an outage ended (decision D186): `unavailable` when the service answered it could not
	 * make it, `unknown` when no answer came; null for anything else, a refusal or an internal error among them
	 */
	static change(problem: unknown): 'unavailable' | 'unknown' | null {
		const read = ApiProblem.of(problem);
		if (read.quiet) return null;
		if (read.unknown) return 'unknown';
		return read.is('UNAVAILABLE', 'UPSTREAM_UNAVAILABLE') ? 'unavailable' : null;
	}

	/**
	 * Who did not answer, in words: the family's catalog of causes names a product (`CauseName` of `@beyond-js/ui`,
	 * "Beyond Accounts did not answer"); a service the answer does not name has CDN's own words
	 */
	static say(cause: Cause, t: (key: string, values?: Record<string, string>) => string): string {
		return cause === 'upstream' ? t('ui.outage.upstream') : t('ui.outage.named', { name: CauseName.of(cause) });
	}

	/** The evidence of a failed load, for its row of the notice's Technical details */
	static details(problem: unknown): Evidence {
		const read = ApiProblem.of(problem);
		return { request: read.trace.request ?? null, time: read.trace.time, text: `${read.code}: ${read.message}` };
	}

	/** Holds a read, or what changed about it; the latest `run` is always the one tried */
	set(key: string, read: Read) {
		const held = this.#reads.get(key);
		this.#reads.set(key, read);
		if (!held || !Outages.#same(held, read)) this.changed();
	}

	/** Lets go of a read: its view went away */
	delete(key: string) {
		if (this.#reads.delete(key)) this.changed();
	}

	/** One notice per cause, each with the reads it affects, in the order they were first held */
	get said(): Said[] {
		const notices = new Map<Cause, Said>();
		for (const [key, { cause, title, details, run }] of this.#reads) {
			if (!cause) continue;
			const notice = notices.get(cause) ?? { cause, reads: [] };
			notice.reads.push({ key, title, details, run });
			notices.set(cause, notice);
		}
		return [...notices.values()];
	}

	/** Nothing on the page could be read: it declared its body, and every read it holds failed with nothing kept */
	get whole(): boolean {
		const reads = [...this.#reads.values()];
		return this.#bodies > 0 && reads.length > 0 && reads.every(read => read.cause !== null && !read.kept);
	}

	/** Whether a page header draws the notices; without one, the frame draws them at the top of the page */
	get placed(): boolean {
		return this.#regions > 0;
	}

	/** Declares the page's body, which the notice replaces when nothing could be read; returns its release */
	body(): () => void {
		return this.#count(step => (this.#bodies += step));
	}

	/** Declares a page header that draws the notices; returns its release */
	region(): () => void {
		return this.#count(step => (this.#regions += step));
	}

	#count(change: (step: number) => void): () => void {
		change(1);
		this.changed();
		let held = true;
		return () => {
			if (!held) return;
			held = false;
			change(-1);
			this.changed();
		};
	}

	static #same(held: Read, read: Read) {
		const evidence = (value: Evidence | null) => (value ? `${value.request} ${value.time} ${value.text}` : '');
		return held.title === read.title && held.cause === read.cause && held.kept === read.kept && evidence(held.details) === evidence(read.details);
	}
}
